import { AlertOptions, AlertType, CupcakeFlavors } from '@ipreo/ngx-sprinkles';

import {
  BenchmarkCurve,
  BenchmarkCurvePoint,
  BenchmarkProvider,
  IceRatingType,
  MACRatingType,
  MMDRatingType
} from '../types/benchmark.types';
import moment, { Moment } from 'moment';
import { BenchmarkCurveIdentifier } from '../types/user-preferences.types';
import { BenchmarkCurveData } from '../state/benchmark-curves/state';

export interface BenchmarkCurveMapping {
  ratingType: MMDRatingType | MACRatingType | IceRatingType;
  shortName: string;
  identifier: BenchmarkCurveIdentifier;
  provider: BenchmarkProvider;
  typeId?: number;
}

export const curveMappings: BenchmarkCurveMapping[] = [
  {
    ratingType: MMDRatingType.interpolated,
    shortName: 'Interp',
    provider: BenchmarkProvider.mmd,
    identifier: BenchmarkCurveIdentifier.MmdInterpolated,
    typeId: 7
  },
  {
    ratingType: MMDRatingType.tripleA,
    shortName: 'Current',
    provider: BenchmarkProvider.mmd,
    identifier: BenchmarkCurveIdentifier.MmdCurrent,
    typeId: 1
  },
  {
    ratingType: MMDRatingType.tripleAInsured,
    shortName: 'AAAI',
    provider: BenchmarkProvider.mmd,
    identifier: BenchmarkCurveIdentifier.MmdAaaInsured,
    typeId: 3
  },
  {
    ratingType: MMDRatingType.doubleA,
    shortName: 'AA',
    provider: BenchmarkProvider.mmd,
    identifier: BenchmarkCurveIdentifier.MmdAa,
    typeId: 4
  },
  {
    ratingType: MMDRatingType.singleA,
    shortName: 'A',
    provider: BenchmarkProvider.mmd,
    identifier: BenchmarkCurveIdentifier.MmdA,
    typeId: 5
  },
  {
    ratingType: MMDRatingType.bDoubleA,
    shortName: 'BAA',
    provider: BenchmarkProvider.mmd,
    identifier: BenchmarkCurveIdentifier.MmdBaa,
    typeId: 6
  },
  {
    ratingType: MMDRatingType.preRefunded,
    shortName: 'Pre-Re',
    provider: BenchmarkProvider.mmd,
    identifier: BenchmarkCurveIdentifier.MmdPreRe,
    typeId: 2
  },
  {
    ratingType: MACRatingType.interpolated,
    shortName: 'Interp',
    provider: BenchmarkProvider.mac,
    identifier: BenchmarkCurveIdentifier.MacInterpolated,
    typeId: undefined
  },
  {
    ratingType: MACRatingType.tripleA,
    shortName: 'Current',
    provider: BenchmarkProvider.mac,
    identifier: BenchmarkCurveIdentifier.MacCurrent,
    typeId: undefined
  },
  {
    ratingType: IceRatingType.tripleA,
    shortName: 'AAA',
    provider: BenchmarkProvider.ice,
    identifier: BenchmarkCurveIdentifier.IceAaa,
    typeId: 11
  }
];

export function getIdentifierForBenchmark(
  provider: BenchmarkProvider,
  ratingType: MMDRatingType | MACRatingType | IceRatingType
) {
  return curveMappings.find(
    (mapping) =>
      mapping.provider === provider && mapping.ratingType === ratingType
  ).identifier;
}

export function getBenchmarkCurveForIdentifier(
  selection: BenchmarkCurveIdentifier
) {
  return curveMappings.find((mapping) => mapping.identifier === selection);
}

export const getBenchmarkCurveLabel = (
  identifier?: BenchmarkCurveIdentifier
): string => {
  if (identifier) {
    const mapping = getBenchmarkCurveForIdentifier(identifier);

    if (identifier === BenchmarkCurveIdentifier.IceAaa) {
      return `${mapping.provider} CMT`;
    }

    return `${mapping.shortName} ${mapping.provider} (bp)`;
  } else {
    return 'Spread';
  }
};

export const getBenchmarkDataPointByMaturityDate = (
  date: Moment,
  dataPoints: BenchmarkCurvePoint[]
): BenchmarkCurvePoint | undefined => {
  let result: BenchmarkCurvePoint;

  if (dataPoints && dataPoints.length > 0) {
    result = dataPoints.find((point) =>
      point.month
        ? point.year === date.year() && point.month === date.month() + 1
        : point.year === date.year()
    );
  }

  return result;
};

export const getYieldByDate = (data, benchmarkCurve): number => {
  if (data.maturityDate && benchmarkCurve && benchmarkCurve.data) {
    const curvePoint = getBenchmarkDataPointByMaturityDate(
      moment(data.maturityDate),
      benchmarkCurve.data
    );

    return curvePoint ? +curvePoint.yield : undefined;
  }
  return undefined;
};

export const getYieldForICECurveByDate = (
  data: { maturityDate?: string },
  benchmarkCurve: BenchmarkCurve
): number => {
  const yieldForICECurve = getYieldByDate(data, benchmarkCurve);

  if (yieldForICECurve !== undefined) {
    return +(Math.round(yieldForICECurve * 100) / 100).toFixed(2);
  }

  if (!data.maturityDate || !benchmarkCurve?.data) {
    return undefined;
  }

  const momentDate = moment(data.maturityDate);
  const oneYearAhead = momentDate.clone().add(1, 'year');

  const curvePoint = benchmarkCurve.data.find((point) => {
    const pointYear = point.year;
    const pointMonth = point.month || 1;
    const pointDate = moment({
      year: pointYear,
      month: pointMonth - 1,
      day: 1
    });

    return (
      pointDate.isAfter(momentDate, 'day') &&
      pointDate.isSameOrBefore(oneYearAhead, 'day')
    );
  });

  return curvePoint
    ? +(Math.round(+curvePoint.yield * 100) / 100).toFixed(2)
    : undefined;
};

export function getCurveForDate(
  dealDate: string,
  curves: BenchmarkCurve[]
): BenchmarkCurve {
  const curveDates: Moment[] = curves
    .map((curve) => curve.date)
    .sort((a, b) => (a.isBefore(b) ? 1 : -1));
  const dealDateMoment: Moment = moment(dealDate, 'YYYY-MM-DD', true);
  const dealCurves = curveDates.filter((item) =>
    item.isSameOrBefore(dealDateMoment)
  );
  const curveDate =
    dealCurves && dealCurves.length ? dealCurves[0] : curveDates[0];

  return curves.find(
    (curve) =>
      curve.date.format('YYYY-MM-DD') === curveDate.format('YYYY-MM-DD')
  );
}

export const NO_MMD_PERMISSIONS_NOTIFICATIONS: AlertOptions = {
  body:
    'You are not permissioned for MMD data, please reach out to Refinitiv directly, clientservices@tm3.com',
  color: CupcakeFlavors.Warning,
  timeout: 8000,
  icon: 'fa fa-exclamation-triangle',
  alertType: AlertType.toast
};

export function shouldLoad(
  state: BenchmarkCurveData,
  date: string,
  provider: BenchmarkProvider
) {
  const noCurve = !state.error && !state.loading && !state.loaded;
  const wrongDate = state.date !== date;
  const wrongProvider = state.provider !== provider;

  return noCurve || wrongDate || wrongProvider;
}

export function isCurveAlwaysAccessible(curve: BenchmarkCurveIdentifier) {
  return !curve.startsWith('MMD');
}
